import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CategoryViewPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategory();
  }, [id]);

  const fetchCategory = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/commerce/categories`, { withCredentials: true });
      const found = res.data.find(c => c.id === id);
      setCategory(found || null);
    } catch (error) {
      console.error('[Category] Error:', error);
      toast.error('Failed to load category');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#050814', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F5F7FF' }}>
        Loading category...
      </div>
    );
  }

  if (!category) {
    return (
      <div style={{ minHeight: '100vh', background: '#050814', padding: '2rem', color: '#F5F7FF' }}>
        Category not found
      </div>
    );
  }

  const getTrendIcon = () => {
    if (category.trend === 'up') return <TrendingUp size={24} color="#2EE6A6" />;
    if (category.trend === 'down') return <TrendingDown size={24} color="#FF4D5A" />;
    return <Minus size={24} color="#8A90A2" />;
  };

  const getScoreColor = (score) => {
    if (score >= 0.7) return 'hsl(var(--success))';
    if (score >= 0.5) return 'hsl(var(--warning))';
    return 'hsl(var(--destructive))';
  };

  return (
    <div style={{ minHeight: '100vh', background: 'hsl(var(--background))', color: 'hsl(var(--foreground))', padding: '2rem' }}>
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/commerce')}
        className="mb-6 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
      >
        <ArrowLeft size={16} className="mr-2" />
        Back to Dashboard
      </Button>

      {/* Category Header */}
      <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', marginBottom: '1.5rem' }}>
        <CardContent className="p-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                {category.name}
              </h1>
              <p style={{ fontSize: '0.875rem', color: '#8A90A2' }}>
                Category Intelligence
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ 
                fontSize: '3rem', 
                fontWeight: 700,
                fontFamily: 'monospace',
                color: getScoreColor(category.compositeScore),
                lineHeight: 1
              }}>
                {Math.round(category.compositeScore * 100)}
              </div>
              <div className="flex items-center gap-2 justify-end mt-2" style={{
                color: category.trend === 'up' ? '#2EE6A6' : category.trend === 'down' ? '#FF4D5A' : '#8A90A2'
              }}>
                {getTrendIcon()}
                <span style={{ fontSize: '1rem', fontWeight: 600, fontFamily: 'monospace' }}>
                  {category.delta >= 0 ? '+' : ''}{category.delta.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 7-day trend */}
      <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}>
        <CardContent className="p-6">
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1.5rem' }}>
            7-Day Trend
          </h3>
          <div className="flex gap-3">
            {category.history7d.map((score, idx) => (
              <div key={idx} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ 
                  height: 100,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  marginBottom: '0.5rem'
                }}>
                  <div style={{ 
                    width: '100%',
                    height: `${score * 100}%`,
                    background: getScoreColor(score),
                    borderRadius: '0.25rem',
                    transition: 'height 0.3s ease'
                  }} />
                </div>
                <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--foreground))', fontFamily: 'monospace' }}>
                  {Math.round(score * 100)}
                </div>
                <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>
                  Day {idx + 1}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
